/**
 * React Query Client Configuration
 *
 * Configures the TanStack Query client with sensible defaults for the Mellify platform:
 * - staleTime: 2 minutes (data considered fresh for 2 min)
 * - gcTime: 10 minutes (garbage collect unused cache entries after 10 min)
 * - retry: 3 attempts with exponential backoff for server errors
 * - refetchOnWindowFocus: only when data is stale
 * - Global error handler for unhandled query errors
 */

import { QueryClient, type DefaultOptions } from '@tanstack/react-query';
import { ApiError } from '../services/api/errors';

/**
 * Determine whether a failed request should be retried.
 * - Do NOT retry on 4xx client errors (except 408 Request Timeout and 429 Rate Limited)
 * - DO retry on network errors and 5xx server errors
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const maxRetries = 3;

  if (failureCount >= maxRetries) return false;

  if (error instanceof ApiError) {
    // Don't retry auth errors, validation errors, not found, forbidden, conflict
    if ([401, 403, 404, 409, 422].includes(error.status)) {
      return false;
    }
    // Retry rate limit errors and timeouts with backoff
    if ([408, 429].includes(error.status)) {
      return true;
    }
    // Retry server errors
    if (error.isServerError || error.isNetworkError) {
      return true;
    }
    return false;
  }

  // For unknown errors, retry up to max
  return true;
}

/**
 * Calculate retry delay with exponential backoff.
 * Base delay: 1s, max delay: 30s.
 */
function retryDelay(attemptIndex: number): number {
  const baseDelay = 1000;
  const maxDelay = 30_000;
  const delay = Math.min(baseDelay * 2 ** attemptIndex, maxDelay);
  // Add jitter (±25%) to prevent thundering herd
  const jitter = delay * 0.25 * (Math.random() * 2 - 1);
  return delay + jitter;
}

/**
 * Default options for all queries and mutations.
 */
const defaultOptions: DefaultOptions = {
  queries: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: shouldRetry,
    retryDelay,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: true,
    refetchOnMount: true,
  },
  mutations: {
    retry: false, // Don't auto-retry mutations
  },
};

/**
 * Create the QueryClient singleton.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions,
  });
}

/**
 * Singleton QueryClient instance for the application.
 */
export const queryClient = createQueryClient();